import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Save, Download, FileText, ChevronDown, ChevronRight, Bold, Italic, Underline, List, ListOrdered, Type } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Minimal rich text editor using contentEditable
const RichEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (editorRef.current && !initialized.current) {
      editorRef.current.innerHTML = value || '';
      initialized.current = true;
    }
  }, [value]);

  // Sync external value changes (e.g., after save/fetch)
  const lastExternalValue = useRef(value);
  useEffect(() => {
    if (value !== lastExternalValue.current && editorRef.current) {
      const selection = window.getSelection();
      const hadFocus = document.activeElement === editorRef.current;
      if (!hadFocus) {
        editorRef.current.innerHTML = value || '';
      }
      lastExternalValue.current = value;
    }
  }, [value]);

  const exec = (command, val = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    handleInput();
  };

  const handleInput = () => {
    const html = editorRef.current?.innerHTML || '';
    lastExternalValue.current = html;
    onChange(html);
  };

  const isActive = (command) => {
    try { return document.queryCommandState(command); } catch { return false; }
  };

  const ToolBtn = ({ cmd, icon: Icon, label }) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); exec(cmd); }}
      className={`p-1.5 rounded transition-colors ${isActive(cmd) ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'text-white/40 hover:text-white/70 hover:bg-white/5'}`}
      title={label}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );

  return (
    <div className="border border-white/10 rounded-lg overflow-hidden bg-white/[0.02]">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-white/10 bg-white/[0.03]">
        <ToolBtn cmd="bold" icon={Bold} label="Bold" />
        <ToolBtn cmd="italic" icon={Italic} label="Italic" />
        <ToolBtn cmd="underline" icon={Underline} label="Underline" />
        <div className="w-px h-4 bg-white/10 mx-1" />
        <ToolBtn cmd="insertUnorderedList" icon={List} label="Bullet List" />
        <ToolBtn cmd="insertOrderedList" icon={ListOrdered} label="Numbered List" />
        <div className="w-px h-4 bg-white/10 mx-1" />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); exec('formatBlock', '<h3>'); }}
          className="p-1.5 rounded text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors"
          title="Heading"
        >
          <Type className="w-3.5 h-3.5" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        data-placeholder={placeholder || 'Start typing...'}
        className="min-h-[180px] max-h-[400px] overflow-y-auto px-4 py-3 text-white/80 text-sm leading-relaxed focus:outline-none
          [&>p]:mb-2 [&>ul]:ml-4 [&>ul]:list-disc [&>ol]:ml-4 [&>ol]:list-decimal [&>li]:mb-1
          [&>h3]:text-white [&>h3]:font-semibold [&>h3]:text-base [&>h3]:mb-2
          [&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-white/20"
        suppressContentEditableWarning
      />
    </div>
  );
};

export const BusinessPlanTab = ({ getStoredPassword }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const passwordRef = useRef(null);

  useEffect(() => {
    passwordRef.current = getStoredPassword();
    const fetchPlan = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/business-plan?password=${passwordRef.current}`);
        if (res.ok) {
          const data = await res.json();
          setPlan(data);
          // Expand all sections by default
          const expanded = {};
          (data.sections || []).forEach(s => { expanded[s.id] = true; });
          setExpandedSections(expanded);
        }
      } catch (e) { toast.error('Failed to load business plan'); }
      setLoading(false);
    };
    fetchPlan();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePlan = useCallback(async () => {
    if (!plan) return;
    setSaving(true);
    const pw = passwordRef.current || localStorage.getItem('adminPassword');
    try {
      const res = await fetch(`${API_URL}/api/admin/business-plan?password=${pw}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(plan)
      });
      if (res.ok) toast.success('Business plan saved');
      else toast.error('Failed to save');
    } catch (e) { toast.error('Failed to save'); }
    setSaving(false);
  }, [plan]);

  const downloadPlan = async (format) => {
    setDownloading(format);
    const pw = passwordRef.current || localStorage.getItem('adminPassword');
    try {
      // Save first to ensure latest content is downloaded
      await fetch(`${API_URL}/api/admin/business-plan?password=${pw}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(plan)
      });
      // Use hidden iframe for reliable download (avoids popup blockers)
      const downloadUrl = `${API_URL}/api/admin/business-plan/download?password=${pw}&format=${format}`;
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = downloadUrl;
      document.body.appendChild(iframe);
      setTimeout(() => document.body.removeChild(iframe), 10000);
      toast.success(`Downloading ${format.toUpperCase()}...`);
    } catch (e) { toast.error('Download failed'); }
    setDownloading(null);
  };

  const updateSection = (sectionId, field, value) => {
    setPlan(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id === sectionId ? { ...s, [field]: value } : s
      )
    }));
  };

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) return <div className="text-center text-white/50 py-8">Loading...</div>;
  if (!plan) return <div className="text-center text-white/50 py-8">Failed to load business plan.</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg text-white">Business Plan</h3>
          <p className="text-white/40 text-sm">Edit and download your strategic business plan</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => downloadPlan('pdf')}
            variant="outline"
            size="sm"
            className="border-white/10 text-white/60 hover:text-white hover:bg-white/5 h-9"
            disabled={!!downloading}
            data-testid="download-pdf-btn"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" />
            {downloading === 'pdf' ? 'Generating...' : 'PDF'}
          </Button>
          <Button
            onClick={() => downloadPlan('docx')}
            variant="outline"
            size="sm"
            className="border-white/10 text-white/60 hover:text-white hover:bg-white/5 h-9"
            disabled={!!downloading}
            data-testid="download-docx-btn"
          >
            <FileText className="w-3.5 h-3.5 mr-1.5" />
            {downloading === 'docx' ? 'Generating...' : 'Word'}
          </Button>
          <Button
            onClick={savePlan}
            className="bg-[#D4AF37] text-black hover:bg-[#B8941E] h-9"
            disabled={saving}
            data-testid="save-business-plan-btn"
          >
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Plan'}
          </Button>
        </div>
      </div>

      {/* Plan Title */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-4">
        <label className="text-white/50 text-xs block mb-1.5">Document Title</label>
        <Input
          value={plan.title || ''}
          onChange={(e) => setPlan({ ...plan, title: e.target.value })}
          className="bg-white/5 border-white/10 text-white text-sm font-medium"
          data-testid="plan-title-input"
        />
      </div>

      {/* Sections */}
      {plan.sections?.map((section) => (
        <div key={section.id} className="bg-white/5 border border-white/10 rounded-lg overflow-hidden" data-testid={`section-${section.id}`}>
          {/* Section Header */}
          <button
            onClick={() => toggleSection(section.id)}
            className="w-full flex items-center justify-between px-5 py-3 hover:bg-white/[0.03] transition-colors"
          >
            <div className="flex items-center gap-3">
              {expandedSections[section.id] ? (
                <ChevronDown className="w-4 h-4 text-[#D4AF37]" />
              ) : (
                <ChevronRight className="w-4 h-4 text-white/30" />
              )}
              <span className="text-white font-medium text-sm">{section.title}</span>
            </div>
            <span className="text-white/20 text-xs">
              {section.content ? `${section.content.replace(/<[^>]*>/g, '').length} chars` : 'Empty'}
            </span>
          </button>

          {/* Section Content */}
          {expandedSections[section.id] && (
            <div className="px-5 pb-5 space-y-3 border-t border-white/5">
              <div className="pt-3">
                <label className="text-white/50 text-xs block mb-1.5">Section Title</label>
                <Input
                  value={section.title}
                  onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                  className="bg-white/5 border-white/10 text-white text-sm"
                  data-testid={`section-title-${section.id}`}
                />
              </div>
              <div>
                <label className="text-white/50 text-xs block mb-1.5">Content</label>
                <RichEditor
                  value={section.content}
                  onChange={(html) => updateSection(section.id, 'content', html)}
                  placeholder={`Write ${section.title.toLowerCase()} content...`}
                />
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Add Section */}
      <button
        onClick={() => {
          const id = `section_${Date.now()}`;
          setPlan(prev => ({
            ...prev,
            sections: [...(prev.sections || []), { id, title: 'New Section', content: '' }]
          }));
          setExpandedSections(prev => ({ ...prev, [id]: true }));
        }}
        className="w-full border border-dashed border-white/10 rounded-lg py-3 text-white/30 hover:text-white/50 hover:border-white/20 transition-colors text-sm"
        data-testid="add-section-btn"
      >
        + Add Section
      </button>
    </div>
  );
};
